import { useClientSideTranslation } from 'src/services/i18n'
import {
  useGetCurrentGame,
  useGetCurrentLanguage,
  useGetGameBetConfig,
  useGetGameConfig
} from 'src/store/game'
import { getSelectionTypeOptions, isBirdieGameType } from 'src/utils/gameTypeUtils'
import { useGetCurrentPlayer } from 'src/store/player'
import { displayAmount } from 'src/utils/displayAmount'
import { calculateRTP } from '../utils/calculateRTP'
import { getBettableSelections, getEffectiveBetStats } from 'src/utils/gameConfigStats'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { isMarbleGame } from 'src/types/domain/Game'
import { effectiveRtp, hasLightning, resolveGameKey } from 'src/utils/effectiveRtp'
import { selectRoundOdds } from 'src/components/BirdieRoundCard/roundOdds'
import { birdieRtpRow } from './birdieRtpRow'

type Props = Readonly<{
  gameType: string
}>

export function SingleRtpTable({ gameType }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage(), 'gameRules')
  const selectionOptions = getSelectionTypeOptions(gameType, t)

  const player = useGetCurrentPlayer()
  const betConfig = useGetGameBetConfig(player)
  const gameConfig = useGetGameConfig()
  const game = useGetCurrentGame()

  if (!betConfig || !isKnownGameConfig(gameConfig)) return null

  // Birdie prices its board per round, so the template-market scan below
  // would quote a max payout no player can see. Quote the constant RTP, the same
  // effective lift every other game shows, and THIS round's ceiling, labelled as varying (A56). No heading of its own
  // (5.10): the only place this branch renders is the Birdie rules section's
  // payouts card, whose title already names the table.
  if (isBirdieGameType(gameType)) {
    const row = birdieRtpRow(isMarbleGame(game) ? selectRoundOdds(game.races[0]) : undefined, t)
    return (
      <div className="rtp-table">
        <table>
          <thead>
            <tr>
              <th>{t('Selections')}</th>
              <th>{t('Max Payout')}</th>
              <th>{t('RTP')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('1 {{singular}}', { singular: selectionOptions.singular })}</td>
              <td>{row.maxPayout}</td>
              <td>{row.rtp}</td>
            </tr>
            {row.effective !== undefined && (
              <tr className="separator-row effective-rtp-row">
                <td colSpan={3}>
                  ⚡ {t('Effective RTP')}*: {row.effective}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  // `game.id` is the race-configuration id, which is what the pricing
  // table keys on. Null for a game we have no verified pricing for —
  // everything below then falls back to base figures.
  const gameKey = resolveGameKey(isMarbleGame(game) ? game.id : undefined)

  // Effective stats — handles both betting modes:
  //   - Classic: pulls maxMultiplier + minProbability off `bet`.
  //   - Chip-placement: scans `markets[]` for the highest-multiplier
  //     option and its probability (since `bet.maxMultiplier` is 0
  //     for chip-placement and `bet.probabilities` is empty).
  // `maxMultiplier` includes the legendary 5x lightning factor for the
  // headline `Max Payout` column ONLY when the game actually has
  // Lightning configured. The RTP column uses the BASE multiplier so a
  // rare-event boost doesn't overstate the typical RTP.
  const stats = getEffectiveBetStats(gameConfig, undefined, gameKey)
  if (stats.baseMaxMultiplier === 0 || stats.rtpProbability === 0) return null

  const baseRtp = calculateRTP(stats.rtpProbability, stats.baseMaxMultiplier)

  // Effective RTP is multiplicative on base and differs PER SELECTION —
  // Stairpong's pink sits in every boost pool (91.64%) while white sits
  // in none (90.62%). We compute it for every bettable selection (the
  // label is the marble colour, which is what the engine's boost map
  // keys on) and quote the span, matching the rtp_effective_min/max
  // production publishes for the same game.
  //
  // A span rather than the headline row's own figure, because the
  // headline row isn't always representative: the CCTV counters put
  // their 36x Exact Boundary bet on top, and that bet's ids are never
  // in the boost pool — quoting only it reported no Lightning at all
  // for a game whose Race Winner market is boosted to 92.16%.
  //
  // Note this is a span of REAL per-selection figures, unlike the
  // retired +1..+8-by-probability-bucket band, which described no bet a
  // player could actually place and put Survivor above 100%.
  const labelFor = (optionId: string) =>
    isMarbleGame(game) ? (game.marbles.find((m) => m.uuid === optionId)?.color ?? '') : ''

  const selections =
    getBettableSelections(gameConfig).length > 0
      ? getBettableSelections(gameConfig)
      : [{ id: stats.rtpOptionId ?? '', baseRtp }]

  const scored = selections
    .map((s) => ({
      base: s.baseRtp,
      effective: effectiveRtp(gameKey, s.baseRtp, labelFor(s.id), 1)
    }))
    .filter((s): s is { base: number; effective: number } => s.effective !== null)

  const effectiveMin = scored.length > 0 ? Math.min(...scored.map((s) => s.effective)) : null
  const effectiveMax = scored.length > 0 ? Math.max(...scored.map((s) => s.effective)) : null

  // Show only when the game has Lightning AND it lifts at least one
  // selection. Coin Flip has none at all, so the row stays off. Keyed on
  // a real lift rather than on the span being non-empty, so a game whose
  // selections are boosted uniformly still shows its figure.
  const showEffective = hasLightning(gameKey) && scored.some((s) => s.effective > s.base)

  const effectiveLabel =
    effectiveMin !== null && effectiveMax !== null
      ? effectiveMin === effectiveMax
        ? `${effectiveMax.toFixed(2)}%`
        : `${effectiveMin.toFixed(2)}-${effectiveMax.toFixed(2)}%`
      : null

  return (
    <>
      <h4 className="yellow">{t('Pick Winner Mode')}</h4>
      <div className="rtp-table">
        <table>
          <thead>
            <tr>
              <th>{t('Selections')}</th>
              <th>{t('Max Payout')}</th>
              <th>{t('RTP')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('1 {{singular}}', { singular: selectionOptions.singular })}</td>
              <td>{displayAmount(stats.maxMultiplier)}x</td>
              <td>{displayAmount(baseRtp)}%</td>
            </tr>
            {showEffective && (
              <tr className="separator-row effective-rtp-row">
                <td colSpan={3}>
                  ⚡ {t('Effective RTP')}*: {effectiveLabel}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}
